import React, { useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import { isEmpty, snakeCase } from 'lodash-es';

import ActivityIcon from 'components/ActivityIcon';
import Button from '@strava/ui/Button';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import {
  pageProps,
  viewingAthleteProps,
  cursorDataProps,
  feedTypeProps,
  indexProps,
  timeAndLocationProps,
  mapProps,
  photoListProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../shared/FeedEntityContext';
import KudosAndCommentsContext from '../../../shared/KudosCommentsAndAchievements/KudosAndCommentsContext';
import {
  feedEntityInit,
  feedEntityReducer
} from '../shared/feedEntity.reducer';
import { buildInitialFeedState } from '../shared/feedEntity.init-state';
import { buildInitialKudosAndCommentsState } from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.initial-state';
import {
  initKudosAndCommentsState,
  kudosAndCommentsReducer
} from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.reducer';
import LocationAndTime from '../shared/LocationAndTime';
import { getPhotoWithMaxDimension } from '../../utils';
import CommentDeleteAlertDialog from '../../../shared/KudosCommentsAndAchievements/CommentDeleteAlertDialog';
import { OPEN_MODAL_OTHER_ATHLETES_TAB } from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.actions';
import useFeedIntersectionObserver from '../../hooks/useFeedIntersectionObserver';

import Card from '../shared/media/Card';
import EntryHeader from '../shared/media/EntryHeader';
import Media from '../shared/media/Media';
import MediaLeft from '../shared/media/MediaLeft';
import MediaBody from '../shared/media/MediaBody';
import EntryMedia from '../shared/media/EntryMedia';
import EntryBlock from '../shared/media/EntryBlock';

import GroupActivityEntry from './GroupActivityEntry';
import styles from './styles.scss';

const GroupActivity = ({
  viewingAthlete,
  clubId,
  page,
  entity,
  feedType,
  index,
  cursorData,
  rowData,
  athleteProfileId,
  timeAndLocation,
  switchMapAndPhoto,
  photos,
  kudosAndComments,
  embedDropdownEnabled,
  isAutoplayEnabled
}) => {
  const headLineActivity = rowData.activities[0];

  const headlineEntityId = headLineActivity.entity_id
    ? headLineActivity.entity_id.toString()
    : null;

  const activityIds = useCallback(
    () => rowData.activities.map((a) => a.entity_id),
    [rowData.activities]
  );

  const mentionedAthletesByActivity = Object.fromEntries(
    rowData.activities.map((a) => {
      return [a.entity_id, a.description_mentioned_athlete_ids || []];
    })
  );

  const analyticProps = {
    page,
    entity,
    athleteProfileId,
    entityId: headlineEntityId,
    activityIds: activityIds(),
    feedType,
    index,
    viewingAthleteId: viewingAthlete.id,
    clubId,
    properties: {
      mentioned_athletes: mentionedAthletesByActivity
    }
  };

  const { inView, intersectionObserverRef } = useFeedIntersectionObserver({
    index,
    isParentFeedEntry: true
  });

  const { trackAnalytics } = useFeedAnalytics({
    ...analyticProps,
    inView
  });

  const commentsByActivityId = useCallback(() => {
    const commentsHash = {};
    if (!isEmpty(kudosAndComments)) {
      Object.entries(kudosAndComments).forEach((entry) => {
        commentsHash[entry[0].toString()] = entry[1].comments;
      });
    }
    return commentsHash;
  }, [kudosAndComments]);

  const [feedEntityState, feedDispatch] = useReducer(
    feedEntityReducer,
    buildInitialFeedState({
      trackAnalytics,
      entity: snakeCase(entity),
      parentEntityId: headlineEntityId,
      isAutoplayEnabled
    }),
    feedEntityInit
  );

  const [kudosAndCommentsEntityState, dispatch] = useReducer(
    kudosAndCommentsReducer,
    buildInitialKudosAndCommentsState({
      trackAnalytics,
      comments: commentsByActivityId(),
      entity: snakeCase(entity),
      parentEntityId: headlineEntityId
    }),
    initKudosAndCommentsState
  );

  const kudosAndCommentsProviderState = {
    kudosAndCommentsEntityState,
    dispatch,
    analyticProps
  };

  const feedProviderState = {
    feedEntityState,
    feedDispatch,
    analyticProps
  };

  const getActivityMap = () => {
    let bannerImage;
    if (switchMapAndPhoto) {
      // replace activity map with a photo
      const headlinePhoto = headLineActivity.photos[0];
      if (headlinePhoto) {
        const photoUrl = getPhotoWithMaxDimension(headlinePhoto.urls);
        bannerImage = { url: photoUrl, alt: headlinePhoto.caption };
      } else {
        // fallback (this scenario should ideally not exist in prod)
        bannerImage = { url: '', alt: '' };
      }
    } else {
      bannerImage = rowData.map;
    }

    return bannerImage;
  };

  return (
    <KudosAndCommentsContext.Provider value={kudosAndCommentsProviderState}>
      <FeedEntityContext.Provider value={feedProviderState}>
        <Card
          key={index}
          index={index}
          cursorData={cursorData}
          ref={intersectionObserverRef}
        >
          {/* headline title, activity icon, time and location */}
          <EntryHeader>
            <Media centered={true}>
              {/* activity icon */}
              {headLineActivity.activity_class_name && (
                <MediaLeft key="activity_icon">
                  <div className={styles.activityIcon}>
                    <ActivityIcon
                      data-testid="activity_icon"
                      type={headLineActivity.activity_class_name.toLowerCase()}
                      size="small"
                      className={styles.activityIconWrapper}
                    />
                  </div>
                </MediaLeft>
              )}

              {/* headline title, location and time */}
              <MediaBody key="activity_entry_body">
                <Button
                  variant="text"
                  className={styles.headlineTextBtn}
                  type="button"
                  onClick={() =>
                    dispatch({
                      type: OPEN_MODAL_OTHER_ATHLETES_TAB,
                      payload: {
                        element: 'group_header',
                        selectedEntityId: headlineEntityId
                      }
                    })
                  }
                >
                  <Markup noWrap={true} content={rowData.title} noHtml={true} />
                </Button>

                <LocationAndTime
                  displayDate={timeAndLocation.displayDate}
                  displayDateAtTime={timeAndLocation.displayDateAtTime}
                  location={timeAndLocation.location}
                  timestampFormat={timeAndLocation.timestampFormat}
                />
              </MediaBody>
            </Media>
          </EntryHeader>

          {/* heading map or photo */}
          <EntryMedia className={styles.headlineImage}>
            <PhotosAndMapImage
              activityId={headLineActivity.entity_id.toString()}
              activityMap={getActivityMap()}
              entryType={snakeCase(entity)}
              isRoutable={headLineActivity.is_routable}
              feedName={page}
              isVirtual={headLineActivity.is_virtual}
              isCommute={headLineActivity.is_commute}
              workoutType={headLineActivity.workout_type}
              privacyTagKey={headLineActivity.privacy_tag_key}
            />
          </EntryMedia>

          {/* individual athlete activity entries */}
          <EntryBlock>
            <ul className={styles.listEntries}>
              {rowData.activities.map((activity) => (
                <GroupActivityEntry
                  key={activity.entity_id}
                  entity={entity}
                  page={page}
                  viewingAthlete={viewingAthlete}
                  photos={photos}
                  activity={activity}
                  kudosAndComments={kudosAndComments[activity.entity_id]}
                  embedDropdownEnabled={embedDropdownEnabled}
                />
              ))}
            </ul>
          </EntryBlock>
          <CommentDeleteAlertDialog />
        </Card>
      </FeedEntityContext.Provider>
    </KudosAndCommentsContext.Provider>
  );
};

GroupActivity.propTypes = {
  // feed entity could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['GroupActivity']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  feedType: feedTypeProps.isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  rowData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    map: mapProps,
    activities: PropTypes.arrayOf(
      PropTypes.shape({
        activity_class_name: PropTypes.string,
        entity_id: PropTypes.number,
        activity_map: mapProps,
        is_routable: PropTypes.bool,
        is_commute: PropTypes.bool,
        is_virtual: PropTypes.bool,
        workout_type: PropTypes.string,
        privacy_tag_key: PropTypes.string,
        photos: PropTypes.arrayOf(
          PropTypes.shape({
            urls: PropTypes.shape({}).isRequired,
            caption: PropTypes.string
          })
        )
      })
    )
  }).isRequired,
  photos: PropTypes.shape({
    photoList: photoListProps
  }).isRequired,
  timeAndLocation: timeAndLocationProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  switchMapAndPhoto: PropTypes.bool.isRequired,
  kudosAndComments: PropTypes.shape({}).isRequired,
  embedDropdownEnabled: PropTypes.bool,
  isAutoplayEnabled: PropTypes.bool
};

GroupActivity.defaultProps = {
  embedDropdownEnabled: false,
  isAutoplayEnabled: true
};

export default GroupActivity;
